// Liens directs vers « Dans le Bureau » (30/09/2026) — même prérequis, même modèle que Trade
// (apps/trade/src/lib/lien.ts) : l'accueil d'EtikID « par intention » doit pouvoir ouvrir le Bureau
// À UN ENDROIT, pas seulement à son accueil.
//
//   bureau.etikpharma.com/?onglet=all&labo=Bayer   → « Toutes » les factures, filtrées sur ce fournisseur
//   bureau.etikpharma.com/?onglet=ech&labo=Bayer   → « Échéances à venir » de ce fournisseur
//   bureau.etikpharma.com/?onglet=ech              → toutes les échéances à venir, par date
//
// `onglet` = un identifiant de la table lib/onglets.js (un inconnu est ignoré, jamais deviné).
// `labo` est rapproché des fournisseurs de VOS factures (le menu « Fournisseur ») — voir
// `resoudreFournisseur`. Introuvable : l'écran le dit, et rien n'est filtré au hasard.
//
// ⚠️ Le retour de connexion EtikID revient sur `location.pathname` et efface la barre d'adresse : un
// lien ouvert déconnecté serait perdu. Il est donc gardé en sessionStorage (10 min) le temps de
// l'aller-retour, puis appliqué. main.js le prend AVANT le gate, qui peut partir vers EtikID aussitôt.

import { ONGLETS } from "./onglets.js";

const PARAMS = ["onglet", "labo"];
const CLE = "bureau.lien";
const DUREE_MS = 10 * 60_000;

/** { onglet, labo? } ou null. Un labo sans onglet vise la liste de ses factures (« Toutes »). */
export function lireLien(search) {
  const p = new URLSearchParams(search);
  const o = (p.get("onglet") ?? "").trim().toLowerCase();
  const labo = (p.get("labo") ?? "").trim().slice(0, 120);
  const connu = ONGLETS.some((x) => x.id === o);
  if (!connu && !labo) return null;
  const onglet = connu ? o : "all";
  return labo ? { onglet, labo } : { onglet };
}

/** L'adresse sans les paramètres du lien (les autres, dont ceux d'EtikID, restent). */
export function sansLien(url) {
  const u = new URL(url);
  for (const k of PARAMS) u.searchParams.delete(k);
  return u.pathname + (u.search ? u.search : "") + u.hash;
}

/**
 * Le lien de la barre d'adresse (gardé pour survivre à la connexion, puis retiré de l'adresse), sinon
 * celui gardé avant l'aller-retour EtikID. Ne l'efface pas : `oublierLien()` le fait une fois appliqué.
 */
export function prendreLien(maintenant = Date.now()) {
  const dansUrl = lireLien(location.search);
  if (dansUrl) {
    try { sessionStorage.setItem(CLE, JSON.stringify({ l: dansUrl, t: maintenant })); } catch { /* le lien vaut pour cette page */ }
    history.replaceState(history.state, "", sansLien(location.href));
    return dansUrl;
  }
  try {
    const brut = sessionStorage.getItem(CLE);
    if (!brut) return null;
    const { l, t } = JSON.parse(brut);
    if (!(maintenant - t < DUREE_MS)) { sessionStorage.removeItem(CLE); return null; }
    return l;
  } catch { return null; }
}
export function oublierLien() { try { sessionStorage.removeItem(CLE); } catch { /* rien à oublier */ } }

// Même garde-fou que le tri des factures de la Réception (factures-equipe/rang-fournisseur.ts) : on ne
// rapproche JAMAIS sur un mot isolé ni générique — « EA Pharma » ne doit pas désigner Pharmazon.
const FORMES = new Set(["sa", "sas", "sasu", "sarl", "eurl", "selas", "selarl", "snc", "gmbh", "ltd", "inc", "spa", "bv", "ag"]);
const GENERIQUES = new Set([
  "pharma", "pharmaceutique", "pharmaceutiques", "pharmaceuticals", "pharmacie", "laboratoire", "laboratoires",
  "labo", "labos", "france", "groupe", "group", "groupement", "the", "de", "des", "du", "la", "le", "les", "et",
]);

/** « Laboratoires EA-Pharma SAS » → « laboratoires ea pharma ». */
export function normaliserNom(s) {
  return String(s ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
    .split(/[^\p{L}\p{N}]+/u).filter((m) => m && !FORMES.has(m)).join(" ");
}

/**
 * Le labo tapé face aux fournisseurs des factures :
 *   { fournisseur }       — un seul correspond (nom identique, ou contenu en mots entiers) → filtre exact ;
 *   { plusieurs: [...] }  — plusieurs le contiennent (« Sanofi » : Sanofi Aventis, Sanofi Winthrop…) ;
 *   null                  — aucun, ou le labo ne porte que des mots génériques.
 */
export function resoudreFournisseur(fournisseurs, texte) {
  const n = normaliserNom(texte);
  if (!n || !n.split(" ").some((m) => !GENERIQUES.has(m))) return null;
  const exacts = fournisseurs.filter((f) => normaliserNom(f) === n);
  if (exacts.length === 1) return { fournisseur: exacts[0] };
  const contenus = exacts.length ? exacts : fournisseurs.filter((f) => ` ${normaliserNom(f)} `.includes(` ${n} `));
  if (contenus.length === 1) return { fournisseur: contenus[0] };
  return contenus.length ? { plusieurs: contenus } : null;
}
